import React, { useState } from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import { Box, Flex } from '@components/layout/Grid';
import { H3 } from '@components/typography/Headings';
import { P } from '@components/typography/Paragraph';
import { DL } from '@components/List';
import Icon from '@components/Icon';
import ButtonFavoriteVideoCard from '@components/ButtonFavoriteVideoCard';
import ButtonAddtoPlaylist from '@ecosystems/ButtonAddtoPlaylist';
import ButtonScheduleModal from '@ecosystems/ButtonScheduleModal';
import MenuItemShareModal from '@ecosystems/MenuItemShareModal';
import TermsList from '@components/TermsList';
import GenreInstructorMetadata from '@ecosystems/GenreInstructorMetadata';
import { Instructor, Term } from '@gql/generated';
import {
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
} from 'src/ui/popover';
import { Button } from '@components/ButtonV2';

const Paragraph = styled(P)`
  padding-right: 20px;
  margin-top: 10px;
`;

const ActionButtonWrapper = styled.div`
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

type Props = {
  identifier: string;
  id: string;
  title: string;
  description: string;
  locked: boolean;
  noActions?: boolean;
  genreSlug: string;
  genre: string;
  instructors: Instructor[];
  duration: number;
  needs: Term[];
};
const ProgramVideoMetadata = (props: Props) => {
  const buttonRef = React.useRef(null);
  const [open, setOpen] = useState(false);

  const handleOpenMenu = () => {
    setOpen(true);
  };

  const handleCloseMenu = () => {
    setOpen(false);
  };

  return (
    <Flex
      flexWrap="wrap"
      alignItems="flex-start"
      data-testid="ProgramVideoMetadata"
      mb={[0, 0, 30]}
    >
      <Box order={1} flex={['0 100%', '0 100%', '1 50%']}>
        <H3 as="h1" data-testid="ProgramVideoMetadata--VideoTitle">
          {props.title}
        </H3>
      </Box>
      <Box order={[4, 4, 3]} flex={['0 auto', '0 auto', '0 70%']}>
        <Paragraph
          dangerouslySetInnerHTML={{ __html: props.description }}
          data-testid="ProgramVideoMetadata--VideoDescription"
        />
      </Box>
      <Box
        order={[2, 2, 4]}
        flex={['1 1 100%', '1 1 100%', '0 calc(30% - 30px)']}
        flexWrap="wrap"
        alignItems="flex-start"
        pl={['0px', '0px', '30px']}
      >
        <DL>
          <Box>
            <GenreInstructorMetadata
              genreSlug={props.genreSlug}
              genre={props.genre}
              instructors={props.instructors}
              openInVideos
            />
            {props.duration ? (
              <TermsList
                data-testid="VideoSingleMetadata--Duration"
                label={t`metadata.duration`}
                data={props.duration + t`metadata.min`}
              />
            ) : null}
            {props.needs && props.needs.length > 0 ? (
              <TermsList
                data-testid="VideoSingleMetadata--Needs"
                label={t`metadata.needs`}
                data={props.needs}
              />
            ) : null}
          </Box>
        </DL>
      </Box>
      {!props.noActions ? (
        <Box
          order={5}
          width={[1, 1, '60%']}
          pr={['0px', '0px', '30px']}
          mt="20px"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <ActionButtonWrapper
              className={props.locked ? 'disabled' : ''}
              data-testid="ActionButtonWrapper"
            >
              <ButtonFavoriteVideoCard videoId={props.id} />
            </ActionButtonWrapper>
            <ActionButtonWrapper
              className={props.locked ? 'disabled' : ''}
              data-testid="ActionButtonWrapper"
            >
              <ButtonScheduleModal
                tag={Button}
                tagParams={{
                  link: true,
                  appearance: 'dark',
                  className: 'uppercase !text-xs',
                }}
                resource="video"
                resourceId={props.id}
              />
            </ActionButtonWrapper>
            <Box>
              <ActionButtonWrapper
                className={props.locked ? 'disabled' : ''}
                data-testid="ActionButtonWrapper"
              >
                <Popover>
                  <PopoverTrigger>
                    <Icon name={'ygb-icon-menu-hor'} fontSize="5px" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <ButtonAddtoPlaylist
                      tag={PopoverItem}
                      videoId={props.id}
                      handleCloseParentMenu={handleCloseMenu}
                      anchorElMenu={() => buttonRef}
                      handleOpenParentMenu={() => {
                        handleOpenMenu();
                      }}
                    />
                    <MenuItemShareModal
                      resourceIdentifier={props.identifier}
                      resource={'videos'}
                    />
                  </PopoverContent>
                </Popover>
              </ActionButtonWrapper>
            </Box>
          </Flex>
        </Box>
      ) : null}
    </Flex>
  );
};

export default React.memo(ProgramVideoMetadata);
